import { useApp, useTranslation } from "@next-platform/client-common-core";
import { useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import { Button, ButtonVariant, IconVariants, TabItem } from "@next-platform/client-common-spicy-ui";
import { SubCapabilityKey } from "@next-platform/packages-ts-types";
import { Spinner } from "@nextui-org/react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import PageMetaTags from "@/components/common/PageMetaTags";
import { Text } from "@/components/customComponents";
import SearchClientCharacterListing from "@/components/search/SearchClientCharacterListing";
import Trans from "@/components/Trans/Trans";
import { ROUTES } from "@/constants";
import AnonymousMarketing from "@/pages/AnonymousMarketing/AnonymousMarketing";
import useAuth from "@/services/useAuth";
import { Collection } from "@/types/api";

const EXPLORE_TAB = "explore";
const MY_RULEBOOKS_TAB = "myRulebooks";

const RulebooksExplore = () => {
  const { t } = useTranslation();
  const { applicationName } = useApp();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const isManageAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.RulebookManage);
  const navigate = useNavigate();

  const tabItems = useMemo<TabItem[]>(
    () => [
      { name: EXPLORE_TAB, label: t("rulebook:explore.tabExplore") },
      { name: MY_RULEBOOKS_TAB, label: t("rulebook:explore.tabMyRulebooks") },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (tabName: string) => {
      if (tabName === MY_RULEBOOKS_TAB) {
        navigate(ROUTES.myCreationRulebook);
      }
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    navigate(ROUTES.rulebookEditor);
  }, [navigate]);

  // Wait for auth to settle so we don't briefly flash the anonymous landing to
  // a signed-in user (or vice versa).
  if (isAuthLoading) {
    return <Spinner className="flex h-[50vh] items-center justify-center" />;
  }

  // Anonymous visitors always get the public marketing landing — explore is a signed-in-only page.
  if (!isAuthenticated) {
    return (
      <>
        <PageMetaTags canonicalUrl={`https://docs.${applicationName}.ai/product-guides/rulebook`} />
        <AnonymousMarketing datakey="creations" />
      </>
    );
  }

  return (
    <div data-testid="RulebooksExplore">
      <PageMetaTags canonicalUrl={`https://docs.${applicationName}.ai/product-guides/rulebook`} />
      <div className="flex items-center justify-between px-4 max-mob:px-2">
        <Text h1 className="m-0" variant="xxl">
          <Trans i18nKey="appMenu:chatbots.rulebooks" />
        </Text>
        {isManageAllowed ? (
          <Button
            data-testid="RulebooksExplore-CreateButton"
            variant={ButtonVariant.CONTAINED}
            leftIcon={IconVariants.ADD}
            onClick={handleCreateClick}
          >
            <Trans i18nKey="rulebook:explore.createRulebook" />
          </Button>
        ) : null}
      </div>
      <SearchClientCharacterListing
        collection={Collection.RULEBOOKS}
        defaultSortField={"createdAt:desc"}
        useStoreTagFilters={false}
        tabItems={tabItems}
        activeTab={EXPLORE_TAB}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default RulebooksExplore;
